import { type FC, useEffect, useState } from 'react';
import YourCandidates from './YourCandidates';
import { ApolloProvider } from '@apollo/client';
import {
  type AppConfig,
  type PageConfig,
  createPageConfig,
} from 'src/libs/PageConfig';
import { createPageAnalytics, TrackOnMount } from './analytics';
import { PageLayout } from './PageLayout';
import { EmployerFooter, EmployerHeader } from '@seek/adv-header-footer';
import { HeadingAndSearch } from './HeadingAndSearch';
import { renderTableBodySkeleton } from './TableBodySkeleton';
import { environments } from 'src/siteConfig';
import { TableLayout } from './TableLayout';
import { MainContentError } from './MainContentError';
import { MainContentLayout } from './MainContentLayout';
import { useHeaderFooterContextQuery } from 'src/libs/HeaderFooterContext';
import { ToastProvider } from 'braid-design-system';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

export const YourCandidatesPage: FC<AppConfig> = ({
  site,
  environment,
  locale,
}) => {
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
  const isStaging = environment === environments.staging;

  useEffect(() => {
    setPageConfig(
      createPageConfig({ site, environment, appName: 'your-candidates' }),
    );
  }, [site, environment]);

  const loadingSkeleton = (
    <PageLayout
      header={
        <EmployerHeader
          activeTabId="jobs"
          site={site}
          locale={locale}
          isStaging={isStaging}
        />
      }
      body={
        <MainContentLayout
          searchSection={
            <HeadingAndSearch
              searchTerm={{ value: '', onChange: () => {}, onBlur: () => {} }}
            />
          }
          sortSection={null}
          tableSection={
            <TableLayout body={renderTableBodySkeleton()} pagination={null} />
          }
        />
      }
      footer={
        <EmployerFooter site={site} locale={locale} isStaging={isStaging} />
      }
    />
  );

  if (pageConfig === null) {
    return loadingSkeleton;
  }

  return (
    <ApolloProvider client={pageConfig.graphClient}>
      <ToastProvider>
        <MainPageLayout
          loadingSkeleton={loadingSkeleton}
          pageConfig={pageConfig}
        />
      </ToastProvider>
    </ApolloProvider>
  );
};

const MainPageLayout: FC<{
  loadingSkeleton: JSX.Element;
  pageConfig: PageConfig;
}> = ({ loadingSkeleton, pageConfig }) => {
  const { router, locale, site, environment } = useAppConfig();
  const isStaging = environment === environments.staging;

  const [pageAnalytics, setStateAnalytics] = useState(() =>
    createPageAnalytics({
      locale,
      site,
      userId: null,
      seekAdvertiserId: null,
      trackJs: pageConfig.trackJs,
      environment,
    }),
  );
  const { data, loading, error } = useHeaderFooterContextQuery({
    onCompleted: ({ currentAdvertiser, headerFooterContext }) => {
      if (currentAdvertiser.__typename === 'MissingAdvertiserIdClaimError') {
        window.location.assign(
          router.accountSelect({
            returnUrl: router.yourCandidates(),
          }),
        );
        return;
      }

      let userId: number | null = null;
      let seekAdvertiserId: string | null = null;

      if (
        headerFooterContext?.__typename === 'HeaderFooterContext' &&
        headerFooterContext.user?.id
      ) {
        const parsedUserId = Number.parseInt(headerFooterContext.user.id, 10);
        if (!isNaN(parsedUserId)) {
          userId = parsedUserId;
        }
      }
      if (currentAdvertiser.__typename === 'CurrentAdvertiserContext') {
        seekAdvertiserId = currentAdvertiser.seekId;
      }

      if (pageConfig.trackJs) {
        pageConfig.trackJs.configure({ userId });
        pageConfig.trackJs.addMetadata(
          'seekAdvertiserId',
          seekAdvertiserId || 'unknown',
        );
      }

      setStateAnalytics(
        createPageAnalytics({
          locale,
          site,
          userId,
          seekAdvertiserId,
          trackJs: pageConfig.trackJs,
          environment,
        }),
      );
    },
  });

  if (
    loading ||
    !data ||
    data.currentAdvertiser.__typename === 'MissingAdvertiserIdClaimError'
  ) {
    return loadingSkeleton;
  }

  if (error) {
    return (
      <PageLayout
        header={
          <EmployerHeader
            activeTabId="jobs"
            site={site}
            locale={locale}
            isStaging={isStaging}
            error={error}
          />
        }
        body={
          <TrackOnMount
            trackFn={pageAnalytics.trackView}
            data={{
              event: 'error_page_displayed',
              attributes: { currentPage: 'error_page_displayed' },
            }}
          >
            <MainContentLayout
              searchSection={null}
              sortSection={null}
              tableSection={<MainContentError />}
            />
          </TrackOnMount>
        }
        footer={
          <EmployerFooter site={site} locale={locale} isStaging={isStaging} />
        }
      />
    );
  }

  return (
    <PageLayout
      header={
        <EmployerHeader
          activeTabId="jobs"
          site={site}
          locale={locale}
          isStaging={isStaging}
          identityContext={data.headerFooterContext?.identityContext}
          advertiser={data.headerFooterContext?.advertiser}
          user={data.headerFooterContext?.user}
        />
      }
      body={
        <YourCandidates
          authN={pageConfig.authN}
          baseApiPath={pageConfig.baseApiPath}
          pageAnalytics={pageAnalytics}
        />
      }
      footer={
        <EmployerFooter
          site={site}
          locale={locale}
          isStaging={isStaging}
          identityContext={data.headerFooterContext?.identityContext}
          advertiser={data.headerFooterContext?.advertiser}
        />
      }
    />
  );
};
